import {GetStaticPaths, GetStaticProps} from "next";
import NextImage from "next/image";
import Link from "next/link";
import React from "react";
import {
    Breadcrumbs as Modes0Breadcrumbs,
    ContentArticleReader,
    ContentTitles,
    PageWithSidebarLayout,
    ServiceCategorySidebarNavigator,
    ServicesRow,
    type Crumb,
    type ServiceCategorySidebarNavigatorCategory,
    type ServiceCategorySidebarNavigatorService,
    type ServicesRowTile,
} from "@modes0/ui-components";
import {getServiceCategories, getServiceCategoryBySlug} from "@/content/services";
import {BUSINESS_NAME} from "@/content";
import type {CategoryDetails, Image as ContentImage, SubCategory} from "@/types";
import {PageSeo} from "@modes0/ui-components-seo";
import {seoSiteProfile} from "@/content/seo";

type ServiceCategoryPageProps = {
    categorySlug: string;
};

const brandColor = "#008e0e";
const brandDarkColor = "#134e4a";
const pageBackgroundColor = "#ffffff";
const categoryAccentColours: Record<string, string> = {
    websites: brandColor,
    branding: "#2563eb",
};

const fallbackAccentColours = [brandColor, "#2563eb", "#a16207", "#7c2d12"];

const getImageSrc = (image?: Partial<ContentImage>) => image?.url || image?.original || image?.thumbnail || "";

const getCategoryHref = (category: CategoryDetails) => `/${category.categoryType}/${category.slug}`;

const getCategoryDescription = (category: CategoryDetails) => category.description?.filter(Boolean).join(" ") || "";

const getCategoryAccentColour = (category: CategoryDetails, index: number) =>
    categoryAccentColours[category.key] || fallbackAccentColours[index % fallbackAccentColours.length];

const getServiceHref = (service: SubCategory, category: CategoryDetails) =>
    service.link || `${getCategoryHref(category)}/${service.slug}`;

const getServiceNavigatorItems = (
    category: CategoryDetails,
    accentColor: string
): ServiceCategorySidebarNavigatorService[] =>
    (category.subCategories || []).map((service) => ({
        id: service.id,
        title: service.title,
        description: service.description,
        href: getServiceHref(service, category),
        accentColor,
    }));

const getServiceNavigatorCategories = (activeCategory: CategoryDetails): ServiceCategorySidebarNavigatorCategory[] =>
    getServiceCategories().map((category, index) => {
        const accentColor = category.id === activeCategory.id ? brandColor : getCategoryAccentColour(category, index);

        return {
            id: category.id,
            title: category.name,
            description: getCategoryDescription(category),
            href: getCategoryHref(category),
            services: getServiceNavigatorItems(category, accentColor),
            accentColor,
        };
    });

const getCategoryCrumbs = (category: CategoryDetails): Crumb[] => [
    {title: "Home", href: "/"},
    {title: "Services", href: `/${category.categoryType}`},
    {title: category.name},
];

const getCategoryImage = (category: CategoryDetails): ContentImage | null => {
    const image = category.images?.[0] as Partial<ContentImage> | undefined;
    const src = getImageSrc(image) || category.heroImage?.url || "";

    if (!src) {
        return null;
    }

    const title =
        image?.title || image?.originalTitle || image?.thumbnailTitle || category.heroImage?.title || category.name;
    const alt = image?.alt || image?.originalAlt || image?.thumbnailAlt || category.heroImage?.alt || title;

    return {
        ...image,
        id: image?.id || category.heroImage?.id || category.id,
        url: image?.url || src,
        original: image?.original || src,
        thumbnail: image?.thumbnail || src,
        title,
        alt,
        originalAlt: image?.originalAlt || alt,
        thumbnailAlt: image?.thumbnailAlt || alt,
        thumbnailTitle: image?.thumbnailTitle || title,
    } as ContentImage;
};

const getServiceImage = (service: SubCategory): ServicesRowTile["image"] => {
    const image = service.images?.[0] as Partial<ContentImage> | undefined;
    const heroImage = service.heroImage;
    const src = getImageSrc(image) || heroImage?.url;

    if (!src) {
        return undefined;
    }

    return {
        src,
        url: src,
        original: image?.original || src,
        thumbnail: image?.thumbnail,
        alt: image?.originalAlt || image?.thumbnailAlt || heroImage?.alt || service.title,
        title: image?.originalTitle || image?.thumbnailTitle || heroImage?.title || service.title,
    };
};

const getServiceRowItems = (serviceCategory: CategoryDetails): ServicesRowTile[] =>
    (serviceCategory.subCategories || []).map((service) => ({
        id: service.id,
        title: service.title,
        description: service.description,
        link: getServiceHref(service, serviceCategory),
        image: getServiceImage(service),
        accentColor: brandColor,
        accentDarkColor: brandDarkColor,
    }));

const ServiceCategorySidebar = ({serviceCategory}: {serviceCategory: CategoryDetails}) => (
    <ServiceCategorySidebarNavigator
        key={serviceCategory.slug}
        categories={getServiceNavigatorCategories(serviceCategory)}
        Link={Link}
        title="Browse services"
        description={`Explore ${serviceCategory.name.toLowerCase()} and related services from ${BUSINESS_NAME}.`}
        navTitle="Service categories"
        ctaLabel="Contact us"
        ctaHref="/contact"
        ctaAriaLabel={`Contact ${BUSINESS_NAME} about ${serviceCategory.name}`}
        supportText="Need help choosing the right service?"
        supportLinkLabel="Contact us"
        supportLinkHref="/contact"
        supportLinkAriaLabel={`Contact ${BUSINESS_NAME}`}
        supportTextSuffix=" and we will point you in the right direction."
        itemCtaLabel="View service"
        categoryCtaLabel="View category"
        searchPlaceholder="Search services"
        searchAriaLabel="Search services"
        defaultOpenCategories={[serviceCategory.id]}
        showCategoryDescriptions
        showServiceIcons
        categoryIconToken="layers"
        serviceIconToken="wrench"
        accentColor={categoryAccentColours[serviceCategory.key] || brandColor}
        backgroundColor="#ffffff"
        surfaceColor="#ffffff"
        serviceSurfaceColor="#ffffff"
        textColor="#151515"
        borderColor="rgba(15, 118, 110, 0.18)"
        maxWidth="100%"
        ariaLabel={`${serviceCategory.name} service navigation`}
    />
);

const ServiceCategoryContent = ({serviceCategory}: {serviceCategory: CategoryDetails}) => {
    const categoryImage = getCategoryImage(serviceCategory);

    return (
        <section className="w-full py-10 md:py-14" style={{backgroundColor: pageBackgroundColor}}>
            <PageWithSidebarLayout
                maxWidth="xl"
                gridTemplateColumns={{
                    lg: "2fr 1fr",
                    xl: "2fr 1fr",
                }}
                main={
                    <div className="min-w-0 bg-white px-4 py-5 md:px-6 md:py-6">
                        <div className="mb-6">
                            <Modes0Breadcrumbs
                                crumbs={getCategoryCrumbs(serviceCategory)}
                                homeAccent={brandColor}
                                currentPageAccent={brandColor}
                            />
                        </div>
                        <ContentTitles
                            title={serviceCategory.name}
                            subTitle={getCategoryDescription(serviceCategory)}
                            titleComponent="h1"
                            subTitleComponent="h2"
                            titleClasses="text-4xl md:text-5xl"
                            subTitleClasses="text-lg md:text-xl"
                        />
                        <article className="min-w-0">
                            <ContentArticleReader contentBlocks={serviceCategory.content as never} paddingY="18px" />
                            {categoryImage && (
                                <figure className="mt-8 overflow-hidden rounded-lg border border-black/10 bg-white shadow-sm">
                                    <div className="relative h-[260px] w-full md:h-[420px]">
                                        <NextImage
                                            src={categoryImage.url}
                                            alt={categoryImage.alt || serviceCategory.name}
                                            fill
                                            sizes="(min-width: 1024px) 820px, calc(100vw - 32px)"
                                            className="object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <figcaption className="border-t border-black/10 px-4 py-3 text-sm leading-6 text-neutral-700">
                                        {categoryImage.caption ||
                                            categoryImage.description ||
                                            categoryImage.title ||
                                            `${serviceCategory.name} services by ${BUSINESS_NAME}.`}
                                    </figcaption>
                                </figure>
                            )}
                        </article>
                    </div>
                }
                sidebar={<ServiceCategorySidebar serviceCategory={serviceCategory} />}
            />
        </section>
    );
};

export const getStaticPaths: GetStaticPaths = () => ({
    paths: getServiceCategories().map((category) => ({
        params: {categorySlug: category.slug},
    })),
    fallback: false,
});

export const getStaticProps: GetStaticProps<ServiceCategoryPageProps> = ({params}) => ({
    props: {
        categorySlug: String(params?.categorySlug || ""),
    },
});

export default function ServiceCategoryPage({categorySlug}: ServiceCategoryPageProps) {
    const serviceCategory = getServiceCategoryBySlug(categorySlug);

    if (!serviceCategory) {
        return null;
    }

    return (
        <>
            <PageSeo meta={serviceCategory.metaTags} siteProfile={seoSiteProfile} includeBusinessSchema />
            <ServiceCategoryContent serviceCategory={serviceCategory} />
            <section className="bg-white py-12 md:py-16">
                <ServicesRow
                    items={getServiceRowItems(serviceCategory)}
                    Link={Link}
                    overline={BUSINESS_NAME}
                    title={`${serviceCategory.name} services`}
                    text={serviceCategory.description?.[0]}
                    visibleTileCount={3}
                    accentColor={brandColor}
                    ariaLabel={`${serviceCategory.name} services`}
                />
            </section>
        </>
    );
}
